import { Box, Typography } from '@deere/fuel-react';
import { useContext, useState } from 'react';
import { AlertBox } from '../components/AlertBox';
import { SupportRequestForm } from '../components/SupportRequestForm';
import { AdminContext } from '../context/AdminContext';
import { LoggedInContext } from '../context/LoggedInContext';
import { WorkforceContext } from '../context/WorkforceContext';
import { errorBannerFromResponse } from '../utils/errorBanner';
import styles from './SupportRequestPage.module.css';

const GENERIC_ERROR_MESSAGE =
    'We could not submit your request due to a technical issue. Please try again later.';
const DUPLICATE_MESSAGE =
    'This looks identical to the request you just submitted. If you need to add files, please update the existing ServiceNow ticket or reply to the ticket email.';
const RATE_LIMIT_MESSAGE =
    'You have submitted several requests in a short time. Please wait a few minutes and try again.';
const SUPPORT_UNIT_FALLBACK = 'Unable to determine from Job Bids context';

const toFailedAttachmentNames = (attachmentResults = []) => {
    if (!Array.isArray(attachmentResults)) {
        return [];
    }

    return attachmentResults
        .filter((result) => result?.attached === false && result?.fileName)
        .map((result) => result.fileName);
};

const parseJsonSafe = async (response) => {
    try {
        return await response.json();
    } catch {
        return {};
    }
};

const toDisplayName = (value) => {
    if (typeof value === 'string') {
        return value.trim();
    }

    if (typeof value?.displayName === 'string') {
        return value.displayName.trim();
    }

    return '';
};

const toUniqueValues = (values) =>
    Array.from(new Set(values.map(toDisplayName).filter(Boolean)));

const toWorkforceUnitOptions = (biddingGroup) => {
    if (Array.isArray(biddingGroup)) {
        return toUniqueValues(biddingGroup);
    }

    return toUniqueValues([biddingGroup]);
};

const toSupportUnitOptions = (selectedAdminGroup, biddingGroup) => {
    const adminUnit = toDisplayName(selectedAdminGroup);

    if (adminUnit) {
        return [adminUnit];
    }

    const workforceUnitOptions = toWorkforceUnitOptions(biddingGroup);

    if (workforceUnitOptions.length > 0) {
        return workforceUnitOptions;
    }

    return [SUPPORT_UNIT_FALLBACK];
};

export const SupportRequestPage = () => {
    const { makeRequest } = useContext(LoggedInContext);
    const { selectedAdminGroup } = useContext(AdminContext);
    const { biddingGroup } = useContext(WorkforceContext);

    const [alert, setAlert] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const unitOptions = toSupportUnitOptions(selectedAdminGroup, biddingGroup);
    const prefillUnit = unitOptions.length === 1 ? unitOptions[0] : '';

    const closeAlert = () => {
        setAlert(null);
    };

    const resetForm = () => {
        setFormKey((currentKey) => currentKey + 1);
    };

    const handleSubmit = async (requestDto, files) => {
        if (typeof makeRequest !== 'function') {
            setAlert({ variant: 'error', msg: GENERIC_ERROR_MESSAGE });
            return;
        }

        setSubmitting(true);

        const formData = new FormData();
        formData.append(
            'request',
            new Blob([JSON.stringify(requestDto)], {
                type: 'application/json',
            })
        );
        (files || []).forEach((file) => {
            formData.append('attachments', file);
        });

        try {
            const response = await makeRequest(
                `${process.env.REACT_APP_API_ENDPOINT}/support/incident`,
                {
                    method: 'POST',
                    headers: { 'content-type': null },
                    body: formData,
                }
            );
            const responseBody = await parseJsonSafe(response);

            if (response?.status === 200 && responseBody?.incidentNumber) {
                const failedAttachmentNames = toFailedAttachmentNames(
                    responseBody?.attachmentResults
                );

                if (failedAttachmentNames.length > 0) {
                    setAlert({
                        variant: 'warning',
                        msg: `Ticket ${
                            responseBody.incidentNumber
                        } created. These files couldn't be attached: ${failedAttachmentNames.join(
                            ', '
                        )}. Please reply to the ticket email with them.`,
                    });
                    resetForm();
                    return;
                }

                setAlert({
                    variant: 'success',
                    msg: `Your support request has been created: ${responseBody.incidentNumber}.`,
                    handleClose: closeAlert,
                });
                resetForm();
                return;
            }

            if (response?.status === 409) {
                setAlert({ variant: 'error', msg: DUPLICATE_MESSAGE });
                return;
            }

            if (response?.status === 429) {
                setAlert({ variant: 'error', msg: RATE_LIMIT_MESSAGE });
                return;
            }

            setAlert({
                variant: 'error',
                msg: errorBannerFromResponse(response, GENERIC_ERROR_MESSAGE)
                    .message,
            });
        } catch (err) {
            setAlert({
                variant: 'error',
                msg: errorBannerFromResponse(err, GENERIC_ERROR_MESSAGE)
                    .message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="support-request-page"
            className={styles.parentContainer}
        >
            <Box
                className={styles.contentContainer}
                data-testid="support-page-content"
            >
                {alert && (
                    <AlertBox
                        variant={alert.variant}
                        msg={alert.msg}
                        handleClose={alert.handleClose}
                    />
                )}
                <Box
                    className={styles.headerContainer}
                    data-testid="support-page-header"
                >
                    <Typography variant="h1" className={styles.heading}>
                        Job Bids - Support Request Form
                    </Typography>
                    <Typography>
                        Use only for IT-related requests. For HR administrative
                        help, use your local HR Support Channel.
                    </Typography>
                    <Typography>
                        When you submit this form, our Job Bids support team
                        will see your John Deere ID and the details you provide
                        (and, if ServiceNow matches your profile, your name and
                        email).
                    </Typography>
                    <Typography>
                        Medium & Low Priority — Notifications are sent to our
                        team via email and ServiceNow. These requests are
                        addressed during standard business hours: Monday–Friday,
                        6:00 AM – 6:00 PM CST.
                    </Typography>
                    <Box className={styles.requiredNote}>
                        <span>* </span>
                        Required fields.
                    </Box>
                </Box>
                <Box
                    component="section"
                    className={styles.formSection}
                    data-testid="support-form-section"
                >
                    <SupportRequestForm
                        key={formKey}
                        onSubmit={handleSubmit}
                        prefillUnit={prefillUnit}
                        unitOptions={unitOptions}
                        submitting={submitting}
                    />
                </Box>
            </Box>
        </div>
    );
};
